import type { TickFormatting } from '@/src/features/analytics/useXAxisProps.hook'
import { getDateFnsLocale } from '@/src/shared/application/translation.service'
// GraphTick.tsx
import { format, isValid } from 'date-fns'
import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { appColors } from '../theme/foundations/colors'

interface GraphTickProps {
  tickProps: any
  formatProps: TickFormatting
  transformX?: (x: number, index?: number) => number
  transformY?: (y: number, index?: number) => number
  transformXYear?: (x: number, index?: number) => number
  customValueFormatter?: (value: any, index: number) => string
}

export const GraphTick2 = ({
  tickProps,
  transformX,
  transformY,
  transformXYear,
  formatProps,
  customValueFormatter,
}: GraphTickProps): ReactElement<SVGElement> => {
  const { i18n, t } = useTranslation()
  const dateLocale = getDateFnsLocale(i18n.language)
  const weekPrefix = t('iso_suite.enpi.calendar_week_prefix')
  const { payload, x, y } = tickProps
  const { value, index } = payload
  if (value === undefined || value === null) return <g></g>

  let formattedValue: any
  const { dateFormat = 'dd MM', rotate = false, showYear = false, fontSize = '12px', showTime } = formatProps

  if (customValueFormatter) {
    formattedValue = customValueFormatter(value, index)
  } else {
    const date = value instanceof Date ? value : new Date(value)
    if (!isValid(date)) return <g></g>
    formattedValue =
      dateFormat === 'w'
        ? `${weekPrefix} ${format(date, dateFormat, { locale: dateLocale })}`
        : format(date, dateFormat, { locale: dateLocale })
  }

  // Default positions
  const positionX = transformX ? transformX(Number(x), index) : Number(x)
  const positionY = transformY ? transformY(Number(y), index) : rotate ? Number(y) + 10 : Number(y)
  const rotation = rotate ? 'rotate(45 0 0)' : ''

  const renderTick = () => (
    <g transform={`translate(${positionX},${positionY}) ${rotation}`}>
      <text textAnchor='middle' fill={appColors.gray['400']}>
        <tspan key={`graph-tick-${index}`} x={0} fontSize={fontSize} letterSpacing={'0.24px'}>
          {formattedValue}
        </tspan>
      </text>
    </g>
  )

  const renderYear = () => {
    const date = value instanceof Date ? value : new Date(value)

    // if invalid date, render nothing
    if (!isValid(date)) return <g></g>

    return (
      <g transform={`translate(${transformXYear ? transformXYear(Number(x), index) : positionX},${positionY + 15})`}>
        <text textAnchor='middle' fill={appColors.gray['400']}>
          <tspan key={`graph-tick-year-${index}`} x={0} fontSize={fontSize} letterSpacing={'0.24px'}>
            {format(date, 'yyyy', { locale: dateLocale })}
          </tspan>
        </text>
      </g>
    )
  }

  const renderTime = () => {
    const date = value instanceof Date ? value : new Date(value)

    // if invalid date, render nothing
    if (!isValid(date)) return <g></g>

    return (
      <g transform={`translate(${transformXYear ? transformXYear(Number(x), index) : positionX},${positionY + 15})`}>
        <text textAnchor='middle' fill={appColors.gray['400']}>
          <tspan key={`graph-tick-year-${index}`} x={0} fontSize={fontSize} letterSpacing={'0.24px'}>
            {format(date, 'HH:mm', { locale: dateLocale })}
          </tspan>
        </text>
      </g>
    )
  }

  return (
    <g>
      {renderTick()}
      {showYear && renderYear()}
      {showTime && renderTime()}
    </g>
  )
}
